import React, { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { Download, Share2, X, Loader2 } from 'lucide-react';

// "Postcard" snapshot of the user's actual bookshelf/tree visual (as opposed to
// ShareCard.js's stats-summary card). Renders whichever shelf view is currently
// active into a lofi postcard frame, then offers Web Share API with a
// download-as-PNG fallback for browsers/contexts where sharing isn't available.
//
// Privacy: only the visible shelf snapshot (book covers/titles/authors, which are
// public book metadata, not user PII) plus the user's display name are shown.
// No email, user id, or other account fields are rendered anywhere on the card.
const PostcardShare = ({ shelfRef, viewMode, displayName, onClose }) => {
  const frameRef = useRef(null);
  const shelfImgRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [shelfImg, setShelfImg] = useState(null);
  const [shareSupported] = useState(
    typeof navigator !== 'undefined' && typeof navigator.share === 'function'
  );

  // Two-pass capture: first snapshot the live shelf/tree DOM node on its own
  // (preserves its real computed styles/animations-frozen-in-place), render that
  // as a static <img> inside the postcard frame, then snapshot the whole frame
  // (art + caption + border) as the final postcard. Avoids cloning raw outerHTML,
  // which would lose component-scoped <style>/keyframes and risk duplicate SVG
  // gradient/def ids.
  const buildImage = async () => {
    if (frameRef.current === null || shelfRef?.current == null) return null;

    const shelfDataUrl = await toPng(shelfRef.current, { cacheBust: true, pixelRatio: 2 });
    await new Promise((resolve, reject) => {
      setShelfImg(shelfDataUrl);
      // Wait a frame for React to render the <img>, then wait for it to load.
      requestAnimationFrame(() => {
        const img = shelfImgRef.current;
        if (!img) return resolve();
        if (img.complete) return resolve();
        img.onload = resolve;
        img.onerror = reject;
      });
    });

    return toPng(frameRef.current, { cacheBust: true, pixelRatio: 2 });
  };

  const handleDownload = async () => {
    setIsGenerating(true);
    try {
      const dataUrl = await buildImage();
      if (!dataUrl) throw new Error('No frame to capture');
      const link = document.createElement('a');
      link.download = `leaf-and-letter-postcard-${viewMode}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Failed to generate postcard image', err);
      alert('Could not generate the postcard image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShare = async () => {
    setIsGenerating(true);
    try {
      const dataUrl = await buildImage();
      if (!dataUrl) throw new Error('No frame to capture');
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `leaf-and-letter-postcard-${viewMode}.png`, { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'My Reading Sanctuary',
          text: 'A postcard from my Reading Sanctuary on Leaf & Letter 🌿',
        });
      } else {
        // Web Share API present but can't share files in this context — fall back.
        await handleDownload();
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.error('Failed to share postcard image', err);
        // Fall back to download so the user isn't left with nothing.
        await handleDownload();
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div style={modalOverlay}>
      <div style={modalContent}>
        <div style={header}>
          <h3 style={{ margin: 0 }}>Share a Postcard</h3>
          <button onClick={onClose} style={closeBtn}><X size={20} /></button>
        </div>

        <div style={scrollArea}>
          {/* The Postcard to be Captured */}
          <div ref={frameRef} style={postcardFrame}>
            <div style={frameInner}>
              <div style={shelfStage}>
                {shelfImg ? (
                  <img ref={shelfImgRef} src={shelfImg} alt="Your shelf" style={shelfClone} />
                ) : shelfRef?.current ? (
                  <p style={{ color: '#a1887f', fontStyle: 'italic', fontSize: '0.85rem' }}>Preview generates when you share or download.</p>
                ) : (
                  <p style={{ color: '#a1887f', fontStyle: 'italic' }}>No shelf to show yet.</p>
                )}
              </div>
              <div style={postcardCaption}>
                <span style={captionName}>{displayName || 'A Reader'}'s</span>
                <span style={captionSub}>Reading Sanctuary · {viewMode === 'tree' ? 'Tree of Knowledge' : 'Bookshelf'}</span>
              </div>
              <div style={postcardFooter}>leafandletter.site</div>
            </div>
          </div>
        </div>

        <div style={actions}>
          {shareSupported && (
            <button onClick={handleShare} disabled={isGenerating} style={shareBtn}>
              {isGenerating ? <Loader2 size={18} className="spin" /> : <Share2 size={18} />}
              {isGenerating ? ' Preparing...' : ' Share Postcard'}
            </button>
          )}
          <button onClick={handleDownload} disabled={isGenerating} style={downloadBtn}>
            {isGenerating ? <Loader2 size={18} className="spin" /> : <Download size={18} />}
            {isGenerating ? ' Generating...' : ' Download Image'}
          </button>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-soft)', textAlign: 'center', marginTop: '10px' }}>
            {shareSupported
              ? 'Share directly, or save the postcard to share anywhere.'
              : 'Save this postcard to share on Instagram, Twitter, or Discord.'}
          </p>
        </div>
      </div>
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

// Styles
const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 3000,
  padding: '20px'
};

const modalContent = {
  backgroundColor: 'var(--bg-card)',
  borderRadius: '16px',
  width: '100%',
  maxWidth: '420px',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '90vh'
};

const header = {
  padding: '16px',
  borderBottom: '1px solid var(--border)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: 'var(--text-main)'
};

const closeBtn = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-soft)'
};

const scrollArea = {
  overflowY: 'auto',
  padding: '20px',
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: '#2c2c2c'
};

const postcardFrame = {
  width: '320px',
  backgroundColor: '#f5f0e6',
  backgroundImage: 'linear-gradient(to bottom right, #fdfbf7, #efebe9)',
  borderRadius: '10px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
  overflow: 'hidden',
  position: 'relative',
  border: '10px solid #fffdf9',
  outline: '1px solid #d7ccc8',
  fontFamily: 'serif'
};

const frameInner = {
  padding: '14px 14px 0 14px'
};

const shelfStage = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '220px',
  borderRadius: '6px',
  overflow: 'hidden'
};

const shelfClone = {
  maxWidth: '100%',
  transform: 'scale(0.92)',
  transformOrigin: 'center',
  pointerEvents: 'none',
  borderRadius: '6px'
};

const postcardCaption = {
  textAlign: 'center',
  margin: '10px 0 6px 0',
  display: 'flex',
  flexDirection: 'column'
};

const captionName = {
  fontFamily: 'Georgia, serif',
  color: '#3e2723',
  fontSize: '1.2rem'
};

const captionSub = {
  fontSize: '0.75rem',
  color: '#8d6e63',
  fontStyle: 'italic',
  marginTop: '2px'
};

const postcardFooter = {
  backgroundColor: '#3e2723',
  color: '#efebe9',
  textAlign: 'center',
  padding: '8px',
  fontSize: '0.68rem',
  letterSpacing: '1px',
  marginTop: '10px'
};

const actions = {
  padding: '16px',
  borderTop: '1px solid var(--border)',
  backgroundColor: 'var(--bg-card)',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
};

const baseActionBtn = {
  width: '100%',
  padding: '12px',
  border: 'none',
  borderRadius: '12px',
  fontWeight: 'bold',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px'
};

const shareBtn = {
  ...baseActionBtn,
  backgroundColor: 'var(--accent)',
  color: 'white'
};

const downloadBtn = {
  ...baseActionBtn,
  backgroundColor: 'var(--bg-soft, #efebe9)',
  color: 'var(--text-main)',
  border: '1px solid var(--border)'
};

export default PostcardShare;
